import { verifyEvent } from "nostr-tools/pure"
import { normalizeRelayUrl } from "@/utils/url"
import type { NostrEvent, NostrFilter, RelayState } from "./types"

/** Structural WebSocket interface so the connection is unit testable in Node. */
export interface WebSocketLike {
  readyState: number
  send(data: string): void
  close(code?: number, reason?: string): void
  onopen: ((event?: unknown) => void) | null
  onmessage: ((event: { data: unknown }) => void) | null
  onclose: ((event?: unknown) => void) | null
  onerror: ((event?: unknown) => void) | null
}

export interface RelaySubscriptionHandlers {
  onEvent: (event: NostrEvent) => void
  onEose?: () => void
  onClosed?: (message: string) => void
}

export interface RelayConnectionOptions {
  read?: boolean
  write?: boolean
  connectTimeout?: number
  publishTimeout?: number
  maxReconnectAttempts?: number
  reconnectBaseDelay?: number
  verifyEvents?: boolean
  authenticate?: (challenge: string, relayUrl: string) => Promise<NostrEvent | undefined>
  onStateChange?: (state: RelayState, connection: RelayConnection) => void
  onNotice?: (message: string, connection: RelayConnection) => void
  webSocketFactory?: (url: string) => WebSocketLike
  random?: () => number
}

interface PendingPublish {
  resolve: (result: { ok: boolean; message: string }) => void
  timer: ReturnType<typeof setTimeout>
}

const SOCKET_OPEN = 1

/**
 * One WebSocket connection to one relay.
 *
 * Owns the connection lifecycle (with exponential backoff), NIP-01
 * REQ/CLOSE/EVENT/EOSE/OK/CLOSED/NOTICE, NIP-42 AUTH, optional signature
 * verification and per relay counters. It has no knowledge of React or of the
 * application's data flow.
 */
export class RelayConnection {
  readonly url: string
  state: RelayState = "idle"
  read: boolean
  write: boolean
  latency?: number
  eventsReceived = 0
  lastError?: string
  lastConnectedAt?: number
  lastMessageAt?: number
  /** Reason reported in the last CLOSED message, surfaced in the UI. */
  lastCloseReason?: string

  private readonly options: RelayConnectionOptions
  private socket?: WebSocketLike
  private readonly subscriptions = new Map<
    string,
    { filters: NostrFilter[]; handlers: RelaySubscriptionHandlers }
  >()
  private readonly pendingPublishes = new Map<string, PendingPublish>()
  private outbox: string[] = []
  private attempts = 0
  private manualClose = false
  private connectTimer?: ReturnType<typeof setTimeout>
  private reconnectTimer?: ReturnType<typeof setTimeout>
  private connectStartedAt = 0
  private authAttempts = 0

  constructor(url: string, options: RelayConnectionOptions = {}) {
    const normalized = normalizeRelayUrl(url)
    if (!normalized) throw new Error(`invalid relay URL: ${url}`)
    this.url = normalized
    this.options = options
    this.read = options.read ?? true
    this.write = options.write ?? true
  }

  private get connectTimeout(): number {
    return this.options.connectTimeout ?? 7000
  }

  private get publishTimeout(): number {
    return this.options.publishTimeout ?? 9000
  }

  get connected(): boolean {
    return this.state === "connected"
  }

  connect(): void {
    this.manualClose = false
    if (this.state === "connecting" || this.state === "connected") return
    this.open()
  }

  disconnect(): void {
    this.manualClose = true
    this.clearTimers()
    this.setState("closed")
    this.failPending("relay connection closed")
    this.teardownSocket()
  }

  /** Forces a fresh socket, used by the relay management UI. */
  reconnect(): void {
    this.attempts = 0
    this.manualClose = false
    this.clearTimers()
    this.teardownSocket()
    this.open()
  }

  private clearTimers(): void {
    if (this.connectTimer) clearTimeout(this.connectTimer)
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.connectTimer = undefined
    this.reconnectTimer = undefined
  }

  private setState(state: RelayState): void {
    if (this.state === state) return
    this.state = state
    this.options.onStateChange?.(state, this)
  }


  private open(): void {
    if (this.manualClose) return
    this.setState(this.attempts > 0 ? "reconnecting" : "connecting")
    this.connectStartedAt = Date.now()

    let socket: WebSocketLike
    try {
      socket = this.createSocket()
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : String(error)
      this.scheduleReconnect()
      return
    }
    this.socket = socket

    this.connectTimer = setTimeout(() => {
      if (this.state !== "connected") {
        this.lastError = "connection timed out"
        this.teardownSocket()
        this.scheduleReconnect()
      }
    }, this.connectTimeout)

    socket.onopen = () => {
      this.clearTimers()
      this.latency = Date.now() - this.connectStartedAt
      this.lastError = undefined
      this.attempts = 0
      this.authAttempts = 0
      this.lastConnectedAt = Date.now()
      this.setState("connected")
      // A socket never survives a reconnect, so every live subscription is re-sent.
      for (const [id, { filters }] of this.subscriptions) {
        this.sendOrQueue(["REQ", id, ...filters])
      }
      this.flushOutbox()
    }

    socket.onmessage = (event) => {
      this.lastMessageAt = Date.now()
      if (typeof event.data === "string") this.handleMessage(event.data)
    }

    socket.onerror = () => {
      this.lastError = "websocket error"
      if (!this.manualClose) {
        this.teardownSocket()
        this.scheduleReconnect()
      }
    }

    socket.onclose = () => {
      this.clearTimers()
      if (this.manualClose) {
        this.setState("closed")
        return
      }
      this.lastError = this.lastError ?? this.lastCloseReason ?? "connection closed"
      this.scheduleReconnect()
    }
  }

  private createSocket(): WebSocketLike {
    const factory = this.options.webSocketFactory
    if (factory) return factory(this.url)
    if (typeof WebSocket === "undefined") throw new Error("WebSocket is not available")
    return new WebSocket(this.url) as unknown as WebSocketLike
  }

  private teardownSocket(): void {
    const socket = this.socket
    this.socket = undefined
    if (!socket) return
    socket.onopen = null
    socket.onmessage = null
    socket.onerror = null
    socket.onclose = null
    try {
      socket.close()
    } catch {
      // closing an already closed socket is not an error
    }
  }

  private scheduleReconnect(): void {
    if (this.manualClose) return
    this.teardownSocket()
    const maxAttempts = this.options.maxReconnectAttempts ?? 6
    if (this.attempts >= maxAttempts) {
      this.setState("error")
      this.failPending("relay is unreachable")
      return
    }
    const base = this.options.reconnectBaseDelay ?? 1200
    const random = this.options.random ?? Math.random
    const delay = Math.round(base * 2 ** this.attempts * (1 + random() * 0.3))
    this.attempts += 1
    this.setState("reconnecting")
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined
      this.open()
    }, delay)
  }

  // ------------------------------------------------------------- subscriptions

  /** Registers a REQ on this relay. Re-sends it automatically after a reconnect. */
  subscribe(subId: string, filters: NostrFilter[], handlers: RelaySubscriptionHandlers): void {
    this.subscriptions.set(subId, { filters, handlers })
    this.sendOrQueue(["REQ", subId, ...filters])
  }

  unsubscribe(subId: string): void {
    if (!this.subscriptions.delete(subId)) return
    this.send(["CLOSE", subId])
  }

  // ------------------------------------------------------------------ publish

  /** Sends an already signed event and resolves with the relay's OK answer. */
  publish(event: NostrEvent): Promise<{ ok: boolean; message: string }> {
    if (!this.write) return Promise.resolve({ ok: false, message: "relay is read-only" })
    if (this.state === "error" || this.state === "closed") {
      this.connect()
    }

    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.pendingPublishes.delete(event.id)
        resolve({ ok: false, message: "publish timed out" })
      }, this.publishTimeout)
      this.pendingPublishes.set(event.id, { resolve, timer })
      this.sendOrQueue(["EVENT", event])
    })
  }

  private failPending(reason: string): void {
    for (const [id, pending] of this.pendingPublishes) {
      clearTimeout(pending.timer)
      pending.resolve({ ok: false, message: reason })
      this.pendingPublishes.delete(id)
    }
  }

  private send(message: unknown[]): void {
    if (!this.socket || this.socket.readyState !== SOCKET_OPEN) return
    try {
      this.socket.send(JSON.stringify(message))
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : String(error)
    }
  }

  private sendOrQueue(message: unknown[]): void {
    if (!this.socket || this.socket.readyState !== SOCKET_OPEN) {
      this.outbox.push(JSON.stringify(message))
      if (this.state === "idle" || this.state === "closed" || this.state === "error") this.connect()
      return
    }
    this.send(message)
  }

  private flushOutbox(): void {
    if (this.outbox.length === 0) return
    const pending = this.outbox
    this.outbox = []
    for (const message of pending) {
      if (this.socket?.readyState === SOCKET_OPEN) this.socket.send(message)
      else this.outbox.push(message)
    }
  }

  // ------------------------------------------------------------- message flow

  private handleMessage(raw: string): void {
    let data: unknown
    try {
      data = JSON.parse(raw)
    } catch {
      return
    }
    if (!Array.isArray(data) || typeof data[0] !== "string") return

    switch (data[0] as string) {
      case "EVENT":
        this.handleEvent(data[1] as string, data[2] as NostrEvent)
        return
      case "EOSE":
        this.subscriptions.get(data[1] as string)?.handlers.onEose?.()
        return
      case "OK": {
        const pending = this.pendingPublishes.get(data[1] as string)
        if (!pending) return
        clearTimeout(pending.timer)
        this.pendingPublishes.delete(data[1] as string)
        pending.resolve({
          ok: data[2] === true,
          message: typeof data[3] === "string" ? data[3] : "",
        })
        return
      }
      case "CLOSED": {
        const message = typeof data[2] === "string" ? data[2] : "subscription closed by relay"
        this.lastCloseReason = message
        this.subscriptions.get(data[1] as string)?.handlers.onClosed?.(message)
        return
      }
      case "NOTICE": {
        const message = typeof data[1] === "string" ? data[1] : "relay notice"
        // Operational errors ("too many concurrent REQs", rate limits, auth)
        // are surfaced through the relay status instead of a toast per relay.
        if (/error|too many|concurrent|rate.?limit|blocked|auth|invalid/i.test(message)) {
          this.lastError = message
        }
        this.options.onNotice?.(message, this)
        return
      }
      case "AUTH":
        void this.handleAuth(typeof data[1] === "string" ? data[1] : "")
        return
      default:
        return
    }
  }

  private handleEvent(subId: string, event: NostrEvent): void {
    const subscription = this.subscriptions.get(subId)
    if (!subscription || !event) return
    if (
      typeof event.id !== "string" ||
      typeof event.pubkey !== "string" ||
      typeof event.kind !== "number" ||
      typeof event.created_at !== "number" ||
      !Array.isArray(event.tags) ||
      typeof event.content !== "string" ||
      typeof event.sig !== "string"
    ) {
      return
    }
    if (this.options.verifyEvents !== false && !verifyEvent(event)) {
      this.lastError = `invalid signature on ${event.id.slice(0, 8)}`
      this.options.onNotice?.(this.lastError, this)
      return
    }
    this.eventsReceived += 1
    subscription.handlers.onEvent(event)
  }

  /** NIP-42: answer an AUTH challenge with a signed kind 22242 event. */
  private async handleAuth(challenge: string): Promise<void> {
    if (!challenge) return
    const authenticate = this.options.authenticate
    if (!authenticate) {
      this.options.onNotice?.("relay requires NIP-42 authentication; no signer available", this)
      return
    }
    if (this.authAttempts >= 2) {
      this.options.onNotice?.("relay authentication failed twice, giving up", this)
      return
    }
    this.authAttempts += 1
    try {
      const event = await authenticate(challenge, this.url)
      if (event) this.send(["AUTH", event])
    } catch (error) {
      this.options.onNotice?.(
        `authentication failed: ${error instanceof Error ? error.message : String(error)}`,
        this,
      )
    }
  }
}
